import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMatch } from '../context/MatchContext';
import { getFlagByAlpha3 } from '../utils/tkdCountries';
import NationalityInput from './NationalityInput';
import { updateSession as apiUpdateSession } from '../services/api';

function Header({ timer, onNewMatch, onExport, onImport, onRoundChange,
                  onMarkRound, onDeleteRoundMarker, onSeekToRound,
                  workMode, onWorkModeChange, onOpenVideo,
                  kbMode, onKbModeChange, onOpenKbEditor }) {
  const { state, updateSession } = useMatch();
  const { session, scores, currentRound, roundMarkers = [] } = state;
  const navigate = useNavigate();
  const numRounds = session?.num_rounds || 3;

  const blueName = session?.athlete_blue_name || 'BLU';
  const redName  = session?.athlete_red_name  || 'ROSSO';
  const blueFlag = getFlagByAlpha3(session?.athlete_blue_nationality);
  const redFlag  = getFlagByAlpha3(session?.athlete_red_nationality);

  // ── Modifica dati sessione ─────────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});

  const openEdit = () => {
    setEditForm({
      athlete_blue_name:        session?.athlete_blue_name        || '',
      athlete_blue_nationality: session?.athlete_blue_nationality || '',
      athlete_red_name:         session?.athlete_red_name         || '',
      athlete_red_nationality:  session?.athlete_red_nationality  || '',
      event_name:               session?.event_name               || '',
      event_date:               session?.event_date               || '',
      weight_category:          session?.weight_category          || '',
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    updateSession(editForm);
    if (session?.id) {
      try { await apiUpdateSession(session.id, editForm); } catch (e) { console.error(e); }
    }
    setEditOpen(false);
  };

  const setField = (field, value) => setEditForm(f => ({ ...f, [field]: value }));

  return (
    <>
    <header className="header">
      <div className="header-top">
        {/* Controlli timer */}
        <div className="header-controls" style={{ flex: '0 0 auto' }}>
          <button
            className={`btn btn-sm ${timer.isRunning ? 'btn-warning' : 'btn-success'}`}
            onClick={timer.isRunning ? timer.pause : timer.start}
            title={timer.isRunning ? 'Pausa' : 'Avvia'}
          >
            {timer.isRunning ? '⏸' : '▶'}
          </button>
          <button className="btn btn-sm btn-secondary" onClick={timer.reset} title="Reset timer">
            ⏹
          </button>
        </div>

        {/* Scoreboard centrale */}
        <div className="header-scoreboard">
          <span className="athlete-name blue" title={blueName}>
            {blueFlag && <span style={{ marginRight: 4 }}>{blueFlag}</span>}
            {blueName}
          </span>
          <span className="score-display blue">{scores.blue}</span>
          <span className={`timer-display${timer.isRunning ? ' running' : ''}`}>
            {timer.display}
          </span>
          <span className="score-display red">{scores.red}</span>
          <span className="athlete-name red" title={redName}>
            {redName}
            {redFlag && <span style={{ marginLeft: 4 }}>{redFlag}</span>}
          </span>
        </div>

        {/* Controlli destra */}
        <div className="header-controls" style={{ flex: '0 0 auto' }}>
          {(workMode === 'dual-pc' || workMode === 'ipad') && (
            <button
              className="btn btn-sm btn-primary"
              onClick={onOpenVideo}
              title="Apri finestra video"
            >
              🖥 Video
            </button>
          )}
          <button className="btn btn-sm btn-ghost" onClick={() => navigate('/')} title="Torna alla dashboard">
            ← Home
          </button>
          <button className="btn btn-sm btn-secondary" onClick={onNewMatch} title="Nuovo incontro">
            Nuovo
          </button>
          {session && (
            <button
              className="btn btn-sm btn-primary"
              onClick={() => navigate(`/report/${session.id}`)}
              title="Apri report analisi completa"
            >
              Report
            </button>
          )}
          <button
            className="btn btn-sm btn-ghost"
            onClick={() => navigate('/sessions')}
            title="Lista sessioni salvate"
          >
            Sessioni
          </button>
          <button className="btn btn-sm btn-ghost" onClick={onExport} title="Esporta JSON">
            Esporta
          </button>
          <button className="btn btn-sm btn-ghost" onClick={onImport} title="Importa JSON">
            Importa
          </button>
        </div>
      </div>

      <div className="header-bottom">
        {/* Selettore Round + marker inizio/fine */}
        <div className="round-selector" style={{ alignItems: 'center', gap: 6 }}>
          <span className="text-dim" style={{ fontSize: 11, marginRight: 2 }}>Round:</span>
          {Array.from({ length: numRounds }, (_, i) => i + 1).map(r => {
            const hasStart = roundMarkers.some(m => m.round === r && m.type === 'start');
            const hasEnd   = roundMarkers.some(m => m.round === r && m.type === 'end');
            return (
              <button
                key={r}
                className={`round-btn${currentRound === r ? ' active' : ''}`}
                onClick={() => { onRoundChange(r); onSeekToRound && onSeekToRound(r); }}
                style={{ position: 'relative' }}
                title={`Round ${r}${hasStart ? ` · inizio segnato` : ''}${hasEnd ? ` · fine segnata` : ''} — clicca per andare al round`}
              >
                R{r}
                {(hasStart || hasEnd) && (
                  <span style={{
                    position: 'absolute', top: -3, right: -3,
                    width: 6, height: 6, borderRadius: '50%',
                    background: hasStart && hasEnd ? 'var(--color-green)' : 'var(--color-orange)',
                    border: '1px solid var(--bg-secondary)',
                  }} />
                )}
              </button>
            );
          })}

          {/* Bottoni segna inizio/fine round corrente */}
          {onMarkRound && (
            <>
              <span style={{ color: 'var(--color-text-dim)', margin: '0 2px', fontSize: 14 }}>|</span>
              {(() => {
                const startMarker = roundMarkers.find(m => m.round === currentRound && m.type === 'start');
                const endMarker   = roundMarkers.find(m => m.round === currentRound && m.type === 'end');
                return (
                  <>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => onMarkRound(currentRound, 'start')}
                      title={startMarker ? `Inizio R${currentRound}: ${startMarker.timestamp_display} — clicca per aggiornare` : `Segna inizio R${currentRound}`}
                      style={{ fontSize: 11, padding: '2px 7px', color: startMarker ? 'var(--color-green)' : undefined }}
                    >
                      ▶ {startMarker ? startMarker.timestamp_display : `Inizio R${currentRound}`}
                      {startMarker && (
                        <span
                          onClick={e => { e.stopPropagation(); onDeleteRoundMarker(startMarker.id); }}
                          style={{ marginLeft: 4, color: 'var(--color-text-dim)', cursor: 'pointer', fontWeight: 700 }}
                          title="Rimuovi marker inizio"
                        >×</span>
                      )}
                    </button>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => onMarkRound(currentRound, 'end')}
                      title={endMarker ? `Fine R${currentRound}: ${endMarker.timestamp_display} — clicca per aggiornare` : `Segna fine R${currentRound}`}
                      style={{ fontSize: 11, padding: '2px 7px', color: endMarker ? 'var(--color-orange)' : undefined }}
                    >
                      ⏹ {endMarker ? endMarker.timestamp_display : `Fine R${currentRound}`}
                      {endMarker && (
                        <span
                          onClick={e => { e.stopPropagation(); onDeleteRoundMarker(endMarker.id); }}
                          style={{ marginLeft: 4, color: 'var(--color-text-dim)', cursor: 'pointer', fontWeight: 700 }}
                          title="Rimuovi marker fine"
                        >×</span>
                      )}
                    </button>
                  </>
                );
              })()}
            </>
          )}
        </div>

        {/* Info evento + pulsante modifica */}
        <div className="event-info" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {session?.event_name      && <span>{session.event_name}</span>}
          {session?.weight_category && <span style={{ marginLeft: 4 }}>{session.weight_category}</span>}
          {session?.event_date      && <span style={{ marginLeft: 4 }}>{session.event_date}</span>}
          <button
            className="btn btn-sm btn-ghost"
            onClick={openEdit}
            title="Modifica dati incontro"
            style={{ marginLeft: 4, padding: '2px 7px', fontSize: 12, opacity: 0.6 }}
          >
            ✏
          </button>
        </div>

        {/* Selettore modalità di lavoro + tastiera */}
        <div className="mode-selector">
          <button
            className={`mode-btn${workMode === 'single' ? ' active' : ''}`}
            onClick={() => onWorkModeChange('single')}
            title="Schermo singolo — video + pannello analisi in una sola finestra"
          >
            🖥 Singolo
          </button>
          <button
            className={`mode-btn${workMode === 'dual-pc' ? ' active' : ''}`}
            onClick={() => onWorkModeChange('dual-pc')}
            title="Due schermi PC — video in finestra separata, pannello a tutta larghezza"
          >
            🖥🖥 Due Schermi
          </button>
          <button
            className={`mode-btn${workMode === 'ipad' ? ' active' : ''}`}
            onClick={() => onWorkModeChange('ipad')}
            title="PC + iPad — video sul PC, comandi sull'iPad via rete"
          >
            🖥📱 PC+iPad
          </button>
          <button
            className={`mode-btn${kbMode === 'set2' ? ' active' : ''}`}
            onClick={() => onKbModeChange(kbMode === 'set1' ? 'set2' : 'set1')}
            title={kbMode === 'set2' ? 'Set 2 attivo — clicca per tornare a Set 1' : 'Attiva Set 2 (tastiera esterna)'}
          >
            ⌨ {kbMode === 'set2' ? 'Set 2' : 'Set 1'}
          </button>
          <button
            className="mode-btn"
            onClick={onOpenKbEditor}
            title="Configura shortcut tastiera Set 2"
          >
            ⚙
          </button>
        </div>

        {/* Score per round */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 11 }}>
          {scores.byRound.map(r => (
            <span key={r.round} className="text-dim">
              R{r.round}: <span className="text-blue fw-bold">{r.blue}</span>-<span className="text-red fw-bold">{r.red}</span>
            </span>
          ))}
        </div>
      </div>
    </header>

    {/* ── Modal modifica dati incontro ──────────────────────────────────── */}
    {editOpen && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
        onClick={e => { if (e.target === e.currentTarget) setEditOpen(false); }}
      >
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--color-border-hover)', borderRadius: 10,
          padding: 24, width: 480, maxWidth: '95vw',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)', marginBottom: 18 }}>
            Modifica dati incontro
          </div>

          {/* Atleti */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--color-blue-light)', fontWeight: 600, marginBottom: 5 }}>ATLETA BLU</div>
              <input
                className="form-input blue-accent"
                placeholder="Nome"
                value={editForm.athlete_blue_name}
                onChange={e => setField('athlete_blue_name', e.target.value)}
                style={{ marginBottom: 8 }}
              />
              <NationalityInput
                value={editForm.athlete_blue_nationality}
                onChange={v => setField('athlete_blue_nationality', v)}
                accentColor="var(--color-blue)"
              />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--color-red-light)', fontWeight: 600, marginBottom: 5 }}>ATLETA ROSSO</div>
              <input
                className="form-input red-accent"
                placeholder="Nome"
                value={editForm.athlete_red_name}
                onChange={e => setField('athlete_red_name', e.target.value)}
                style={{ marginBottom: 8 }}
              />
              <NationalityInput
                value={editForm.athlete_red_nationality}
                onChange={v => setField('athlete_red_nationality', v)}
                accentColor="var(--color-red)"
              />
            </div>
          </div>

          {/* Evento */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginBottom: 5 }}>EVENTO</div>
              <input
                className="form-input"
                placeholder="Nome evento"
                value={editForm.event_name}
                onChange={e => setField('event_name', e.target.value)}
              />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginBottom: 5 }}>DATA</div>
              <input
                className="form-input"
                type="date"
                value={editForm.event_date}
                onChange={e => setField('event_date', e.target.value)}
              />
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginBottom: 5 }}>CATEGORIA PESO</div>
            <input
              className="form-input"
              placeholder="Es. -68kg"
              value={editForm.weight_category}
              onChange={e => setField('weight_category', e.target.value)}
            />
          </div>

          {/* Azioni */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => setEditOpen(false)}>Annulla</button>
            <button
              className="btn btn-success"
              onClick={handleEditSave}
              disabled={!editForm.athlete_blue_name.trim() || !editForm.athlete_red_name.trim()}
            >
              Salva
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default Header;
